import { memo, type CSSProperties } from 'react';
import type { IndexPhase } from '../../services/workbook/WorkbookIndexer';

/** The picture above the first index's bar: a small sheet the agent is looking at. While the sheets are read, a
 *  lens sweeps it and the cells it passes light up; once they are in, it turns to the formulas, tracing the cells
 *  each one reads, as Excel's "Trace Precedents" draws them.
 *
 *  Decoration only: the bar and the counts under it say where the run stands, so the picture is hidden from
 *  assistive technology and frozen on one frame for users who asked for reduced motion (index.css `.pc-idx`).
 *
 *  The sheet is one still SVG; what moves sits on HTML layers above it, each the size of the picture and drawn
 *  in the same viewBox. Reading the sheets keeps the main thread busy (Office.js reads, JSON, gzip), and the
 *  webviews animate opacity and transform on HTML elements off that thread, but not on SVG shapes: drawn
 *  inside the SVG, the lens stuttered through the first sheets. */
export const IndexingAnimation = memo(function IndexingAnimation({ phase }: { phase: IndexPhase }) {
  const scene: Scene = phase === 'capturing' || phase === 'idle' ? 'read' : 'discover';
  return (
    <div className="pc-idx relative mx-auto w-full max-w-[280px]" style={{ aspectRatio: `${VIEW_W} / ${VIEW_H}` }} data-testid="indexing-animation" data-scene={scene} aria-hidden="true">
      <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} className="absolute inset-0 h-full w-full" focusable="false">
        <Sheet />
      </svg>
      <div className="pc-idx-read absolute inset-0">
        {READ_CELLS.map(({ c, r, delay }) => (
          <span key={`${c}-${r}`} className="pc-idx-wake absolute rounded-full" style={{ ...percentBox(barRect(c, r)), animationDelay: `${delay}ms` }} />
        ))}
        <Lens />
      </div>
      {/* Mounted at the switch, so the first formula starts there instead of mid-loop. */}
      {scene === 'discover' && (
        <div className="pc-idx-discover absolute inset-0">
          {FRAMES.map((f, i) => <Frame key={f.target} frame={f} index={i} />)}
        </div>
      )}
    </div>
  );
});

const VIEW_W = 240;
const VIEW_H = 128;

/** A viewBox rectangle as a position over the picture, in percentages, for the HTML layers. */
function percentBox(b: { x: number; y: number; width: number; height: number }): CSSProperties {
  return { left: `${(b.x / VIEW_W) * 100}%`, top: `${(b.y / VIEW_H) * 100}%`, width: `${(b.width / VIEW_W) * 100}%`, height: `${(b.height / VIEW_H) * 100}%` };
}

type Scene = 'read' | 'discover';

// Geometry, in viewBox units. The lens keyframes in index.css (`pc-idx-lens`) move it by whole columns and row
// pairs of this grid, 5 × COL_W across and 2 × ROW_H down, written as percentages of VIEW_W and VIEW_H.
const SHEET = { x: 6, y: 4, w: 228, h: 120 };
const GRID_X = 20;
const GRID_Y = 34;
const COL_W = 35;
const ROW_H = 15;
const COLS = 'ABCDEF';

/** Width of each cell's content bar (0: empty). Column A holds labels, left-aligned; the others numbers, right-aligned. */
const CONTENT: number[][] = [
  [22, 14, 0, 0, 0, 0],
  [20, 16, 18, 18, 16, 18],
  [24, 14, 16, 20, 18, 16],
  [18, 16, 20, 16, 14, 18],
  [22, 12, 18, 18, 20, 16],
  [16, 18, 22, 20, 22, 20],
];

const cellX = (c: number) => GRID_X + c * COL_W;
const cellY = (r: number) => GRID_Y + r * ROW_H;
const center = (c: number, r: number) => ({ x: cellX(c) + COL_W / 2, y: cellY(r) + ROW_H / 2 });

function barRect(c: number, r: number) {
  const w = CONTENT[r][c];
  const x = c === 0 ? cellX(c) + 4 : cellX(c) + COL_W - 4 - w;
  return { x, y: cellY(r) + 5.5, width: w, height: 4, rx: 2 };
}

/** Reading loop, ms; mirrors `.pc-idx-lens` and `.pc-idx-wake` in index.css. */
export const READ_MS = 7000;
/** The lens's three sweeps, each over a pair of rows, as [start, end] percentages of READ_MS (`pc-idx-lens`). The
 *  middle one runs right to left. */
const SWEEPS: [number, number][] = [[3, 27], [32, 56], [61, 85]];

/** Each cell with content lights when the lens is over its column, in its pair of rows. */
const READ_CELLS = CONTENT.flatMap((row, r) =>
  row.flatMap((w, c) => {
    if (!w) return [];
    const k = Math.floor(r / 2);
    const [start, end] = SWEEPS[k];
    const f = k % 2 === 0 ? c / 5 : 1 - c / 5;
    return [{ c, r, delay: Math.round(((start + (end - start) * f) / 100) * READ_MS) }];
  }),
);

/** One formula of the discovery loop: the cell it sits in, what the formula bar shows, and the cells or ranges it
 *  reads. Addresses are this little sheet's own. */
interface FormulaFrame {
  target: string;
  formula: string;
  sources: string[];
}

const FRAMES: FormulaFrame[] = [
  { target: 'E6', formula: '=SUM(E2:E5)', sources: ['E2:E5'] },
  { target: 'D4', formula: '=C4*(1+$B$1)', sources: ['C4', 'B1'] },
  { target: 'F3', formula: '=MAX(D3,E3)', sources: ['D3', 'E3'] },
];

/** Discovery loop, ms per formula; mirrors `.pc-idx-frame` in index.css (three frames, 9 s). */
export const FRAME_MS = 3000;
const FRAME_LEAD_MS = 300;

function parseCell(a1: string) {
  return { c: COLS.indexOf(a1[0]), r: Number(a1.slice(1)) - 1 };
}

function Sheet() {
  const { x, y, w, h } = SHEET;
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={6} className="fill-white stroke-line-card" strokeWidth={1} />
      {/* Formula bar: name box, fx, then the formula (per frame). */}
      <line x1={x} x2={x + w} y1={y + 16} y2={y + 16} className="stroke-line-soft" />
      <line x1={x + 28} x2={x + 28} y1={y + 3} y2={y + 13} className="stroke-line-soft" />
      <text x={x + 34} y={y + 11} className="fill-fainter font-mono" fontSize={7} fontStyle="italic">fx</text>
      <rect x={x + 48} y={y + 6} width={70} height={4} rx={2} className="pc-idx-placeholder fill-line-soft" />
      {/* Column and row headers. */}
      <rect x={x} y={y + 16} width={w} height={GRID_Y - y - 16} className="fill-hover" />
      <rect x={x} y={GRID_Y} width={GRID_X - x} height={y + h - GRID_Y - 6} className="fill-hover" />
      {[...COLS].map((l, c) => (
        <text key={l} x={cellX(c) + COL_W / 2} y={GRID_Y - 4} textAnchor="middle" className="fill-fainter font-sans" fontSize={6.5}>{l}</text>
      ))}
      {CONTENT.map((_, r) => (
        <text key={r} x={x + (GRID_X - x) / 2} y={cellY(r) + 10} textAnchor="middle" className="fill-fainter font-sans" fontSize={6.5}>{r + 1}</text>
      ))}
      {/* Gridlines. */}
      {Array.from({ length: COLS.length + 1 }, (_, c) => (
        <line key={`v${c}`} x1={cellX(c)} x2={cellX(c)} y1={y + 16} y2={cellY(CONTENT.length)} className="stroke-line-soft" />
      ))}
      {Array.from({ length: CONTENT.length }, (_, r) => (
        <line key={`h${r}`} x1={x} x2={x + w} y1={cellY(r)} y2={cellY(r)} className="stroke-line-soft" />
      ))}
      <g className="pc-idx-bars">
        {CONTENT.flatMap((row, r) => row.map((w, c) => (w ? <rect key={`${c}-${r}`} className="fill-line" {...barRect(c, r)} /> : null)))}
      </g>
    </g>
  );
}

/** The lens, drawn over the first sweep's starting cell; `pc-idx-lens` moves it from there. */
function Lens() {
  const { x, y } = { x: center(0, 0).x, y: cellY(1) };
  return (
    <div className="pc-idx-lens absolute inset-0">
      <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} className="h-full w-full" focusable="false">
        <g transform={`translate(${x} ${y})`}>
          <circle r={12} className="fill-white stroke-accent" fillOpacity={0.35} strokeWidth={1.6} />
          <line x1={8.6} y1={8.6} x2={15} y2={15} className="stroke-accent" strokeWidth={2.6} strokeLinecap="round" />
        </g>
      </svg>
    </div>
  );
}

function Frame({ frame, index }: { frame: FormulaFrame; index: number }) {
  const delay = { animationDelay: `${FRAME_LEAD_MS + index * FRAME_MS}ms` };
  const t = parseCell(frame.target);
  const to = center(t.c, t.r);
  return (
    <div className={`pc-idx-frame pc-idx-frame-${index} absolute inset-0`} style={delay}>
      <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} className="h-full w-full" focusable="false">
        <text x={SHEET.x + 14} y={SHEET.y + 11} textAnchor="middle" className="fill-muted font-mono" fontSize={6.5}>{frame.target}</text>
        <text x={SHEET.x + 48} y={SHEET.y + 11} className="fill-ink font-mono" fontSize={7.5}>{frame.formula}</text>
        {frame.sources.map((s) => {
          const [a, b = a] = s.split(':').map(parseCell);
          const box = { x: cellX(a.c), y: cellY(a.r), width: (b.c - a.c + 1) * COL_W, height: (b.r - a.r + 1) * ROW_H };
          // Traced from the cell, or from a range's first cell, as Excel draws it.
          const from = center(a.c, a.r);
          return (
            <g key={s}>
              <rect {...box} className="fill-accent-bg stroke-accent" fillOpacity={0.6} strokeWidth={1} strokeDasharray={s.includes(':') ? '3 2' : undefined} />
              <Arrow from={from} to={to} style={delay} />
            </g>
          );
        })}
        <rect x={cellX(t.c)} y={cellY(t.r)} width={COL_W} height={ROW_H} className="fill-none stroke-accent" strokeWidth={1.5} />
        <rect className="pc-idx-pop fill-accent" style={delay} {...barRect(t.c, t.r)} />
      </svg>
    </div>
  );
}

/** A precedent arrow: a curve from source to target that draws itself, then its head and source dot. */
function Arrow({ from, to, style }: { from: { x: number; y: number }; to: { x: number; y: number }; style: CSSProperties }) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy) || 1;
  // Bow the curve sideways by a share of its length, to the side nearer the top of the sheet (left when vertical).
  const bow = Math.min(18, len * 0.35);
  const flip = dx / len > 0 ? -1 : 1;
  const ctrl = { x: (from.x + to.x) / 2 + flip * (-dy / len) * bow, y: (from.y + to.y) / 2 + flip * (dx / len) * bow };
  // Stop short of the target's centre so the head sits on it, pointing along the curve's last tangent.
  const tx = to.x - ctrl.x;
  const ty = to.y - ctrl.y;
  const tl = Math.hypot(tx, ty) || 1;
  const ux = tx / tl;
  const uy = ty / tl;
  const end = { x: to.x - ux * 4, y: to.y - uy * 4 };
  const head = [
    `${to.x},${to.y}`,
    `${end.x - uy * 2.6},${end.y + ux * 2.6}`,
    `${end.x + uy * 2.6},${end.y - ux * 2.6}`,
  ].join(' ');
  return (
    <g>
      <path d={`M${from.x} ${from.y} Q${ctrl.x} ${ctrl.y} ${end.x} ${end.y}`} pathLength={1} className="pc-idx-draw fill-none stroke-accent" strokeWidth={1.2} strokeLinecap="round" style={style} />
      <circle cx={from.x} cy={from.y} r={1.9} className="fill-accent" />
      <polygon points={head} className="pc-idx-pop fill-accent" style={style} />
    </g>
  );
}
